import type { AssetQaCheckDto } from '@/types/assetQa'
import { QA_CHECK_STATUS_META } from '../constants/assetQaMeta'
import { groupChecks, describeGroup, sampleHexes, type AssetQaGroup } from './assetQaGrouping'

interface Props {
    checks: AssetQaCheckDto[]
}

/**
 * Every checklist item and how it came out — passes, flags, items that did not apply, and items
 * nobody could check.
 *
 * Never filtered to the failures. The complete grid is what makes the report honest about coverage:
 * a reader seeing only three flagged items has no way to know whether nineteen checks ran or four
 * did, and "no findings" means something entirely different in each case.
 *
 * ⚑ Coverage is what the grid is FOR, which is why repeated rows are folded rather than listed
 * (ORB-967). The brand-colour check reports once per sampled colour, so a gradient printed 82 rows
 * here for what the matrix lists as one item — "All fonts and colour values used comply with the
 * brand guidelines". Eighty-two rows do not report coverage better than one; they report it worse,
 * because the four items the section actually has are no longer findable among them. The members
 * are still here, one expand away.
 */
export function AssetQaChecklist({ checks }: Props) {
    if (checks.length === 0) return null

    // Grouped in the order the rulebook groups them, preserving first appearance rather than
    // sorting alphabetically — the grid reads in the order the checks were designed to be read.
    const groups = checks.reduce<Record<string, AssetQaCheckDto[]>>((accumulator, check) => {
        const group = accumulator[check.Group] ??= []
        group.push(check)
        return accumulator
    }, {})

    return (
        <div className="space-y-3">
            {Object.entries(groups).map(([group, items]) => (
                <div key={group}>
                    <h5 className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-1.5">
                        {group}
                    </h5>
                    <ul className="space-y-1">
                        {groupChecks(items).map(folded => (
                            <ChecklistRow key={folded.key} folded={folded} />
                        ))}
                    </ul>
                </div>
            ))}
        </div>
    )
}

function ChecklistRow({ folded }: { folded: AssetQaGroup<AssetQaCheckDto> }) {
    const check = folded.lead
    const meta = QA_CHECK_STATUS_META[check.Status]
        ?? QA_CHECK_STATUS_META['Needs Human Verification']
    const { Icon } = meta

    const summary = describeGroup(folded)
    const samples = sampleHexes(folded)

    return (
        <li className="flex items-start gap-2">
            <Icon
                className={`w-3.5 h-3.5 mt-0.5 flex-shrink-0 ${meta.iconClasses}`}
                aria-hidden="true"
            />
            <div className="min-w-0 w-full">
                <p className="text-xs text-slate-700 dark:text-slate-300 break-words">
                    {check.Description}
                    {/* The status in words as well as a colour, so it survives being read by
                        someone who cannot separate amber from green. */}
                    <span className="sr-only"> — {meta.label}</span>
                    {summary && (
                        <span className="ml-1.5 text-slate-500 dark:text-slate-400">· {summary}</span>
                    )}
                </p>

                {/* Why it could not be checked, whenever there is a reason. An unexplained amber
                    teaches people to ignore amber.

                    ⚑ For a folded group the members disagree on the detail — different shades,
                    different distances — so the lead's sentence would be quietly wrong for the other
                    42. The summary above carries what they share, and the expansion carries the
                    rest. A single row still shows its own reason exactly as before. */}
                {!summary && check.Reason && (
                    <p className="text-[11px] text-amber-700 dark:text-amber-400 break-words">
                        {check.Reason}
                    </p>
                )}

                {/* ⚑ Shown for a flagged row too, not only an unverified one. Reason is written
                    only when a check could not be performed, so before ORB-967 a flagged colour row
                    arrived here carrying its description and nothing else — no hex, no distance, no
                    role — which is the unexplained amber the rule above is about.

                    ⚑ The hex and the role, not only the measurement. "ΔE 10.0 · 1.6° from its hue"
                    under "The colours in this asset are on the brand palette" still does not say
                    WHICH colour failed or what it was measured against, which leaves the row barely
                    less unexplained than before. Raised by Copilot on PR #230. */}
                {!summary && !check.Reason && (samples.length > 0 || check.Measured) && (
                    <p className="font-mono text-[11px] text-slate-400 dark:text-slate-500 break-words">
                        {[samples[0], folded.role && `near ${folded.role}`, check.Measured]
                            .filter(Boolean)
                            .join(' · ')}
                    </p>
                )}

                {summary && (
                    <details className="mt-0.5">
                        <summary className="text-[11px] text-slate-500 dark:text-slate-400 cursor-pointer hover:text-slate-700 dark:hover:text-slate-200 focus:outline-none focus:ring-2 focus:ring-primary-500 rounded">
                            {samples.length > 0 && (
                                <span className="font-mono">{samples.join(', ')}</span>
                            )}
                            {samples.length > 0 && folded.members.length > samples.length && ' · '}
                            {folded.members.length > samples.length && 'show all'}
                        </summary>
                        <ul className="mt-1 ml-1 space-y-0.5 border-l border-slate-200 dark:border-slate-700 pl-2">
                            {folded.members.map(member => (
                                <li
                                    key={member.Id}
                                    className="text-[11px] text-slate-500 dark:text-slate-400 break-words"
                                >
                                    {member.Reason ?? member.Measured ?? member.Id}
                                </li>
                            ))}
                        </ul>
                    </details>
                )}
            </div>
        </li>
    )
}
